import { useState, useEffect } from "react"
import { useDropzone } from "react-dropzone"
import { motion, AnimatePresence } from "framer-motion"
import styles from "./UploadPhotos.module.scss"
import { Trash2, CheckCircle, ArrowRight, ArrowLeft, X, Upload, CreditCard, User, Shield } from "lucide-react"

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? -100 : 100,
    opacity: 0,
  }),
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return isMobile
}

const UploadPhotos = ({ handleSendPhotos, closeModal }) => {
  const isMobile = useIsMobile()
  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState(1)
  const [error, setError] = useState("")
  const [files, setFiles] = useState({
    dniFront: null,
    dniBack: null,
    selfie: null,
  })

  const onDrop = (acceptedFiles, field) => {
    const file = acceptedFiles[0]
    const validMimeTypes = ["image/jpeg", "image/png"]

    if (file && validMimeTypes.includes(file.type)) {
      setFiles((prevFiles) => ({
        ...prevFiles,
        [field]: file,
      }))
      setError("")
    } else {
      setError("El tipo de archivo no es válido. Solo se aceptan imágenes en formato JPEG o PNG.")
    }
  }

  const { getRootProps: getRootPropsDniFront, getInputProps: getInputPropsDniFront } = useDropzone({
    multiple: false,
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    onDrop: (acceptedFiles) => onDrop(acceptedFiles, "dniFront"),
  })

  const { getRootProps: getRootPropsDniBack, getInputProps: getInputPropsDniBack } = useDropzone({
    multiple: false,
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    onDrop: (acceptedFiles) => onDrop(acceptedFiles, "dniBack"),
  })

  const { getRootProps: getRootPropsSelfie, getInputProps: getInputPropsSelfie } = useDropzone({
    multiple: false,
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    onDrop: (acceptedFiles) => onDrop(acceptedFiles, "selfie"),
  })

  const handleRemove = (field, event) => {
    event.stopPropagation()
    setFiles((prevFiles) => ({
      ...prevFiles,
      [field]: null,
    }))
  }

  const handleSubmit = () => {
    handleSendPhotos(files)
    closeModal()
  }

  const nextStep = () => {
    setDirection(1)
    setStep((prevStep) => Math.min(prevStep + 1, 2))
  }

  const prevStep = () => {
    setDirection(-1)
    setStep((prevStep) => Math.max(prevStep - 1, 0))
  }

  const isFormValid = files?.dniFront && files?.dniBack && files?.selfie

  const stepConfig = [
    {
      title: "Frente del DNI",
      description: "Sube una foto clara del frente de tu DNI",
      icon: CreditCard,
      field: "dniFront",
      getRootProps: getRootPropsDniFront,
      getInputProps: getInputPropsDniFront,
    },
    {
      title: "Dorso del DNI",
      description: "Sube una foto clara del dorso de tu DNI",
      icon: CreditCard,
      field: "dniBack",
      getRootProps: getRootPropsDniBack,
      getInputProps: getInputPropsDniBack,
    },
    {
      title: "Foto de tu rostro",
      description: "Toma una selfie clara de tu rostro",
      icon: User,
      field: "selfie",
      getRootProps: getRootPropsSelfie,
      getInputProps: getInputPropsSelfie,
    },
  ]

  const renderMobileSteps = () => {
    const currentStep = stepConfig[step]
    const IconComponent = currentStep.icon

    return (
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          className={styles.uploadSection}
          key={step}
          custom={direction}
          initial="enter"
          animate="center"
          exit="exit"
          variants={variants}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.stepIndicator}>
            <div className={styles.stepProgress}>
              <div className={styles.stepProgressBar} style={{ width: `${((step + 1) / 3) * 100}%` }} />
            </div>
            <span className={styles.stepText}>Paso {step + 1} de 3</span>
          </div>

          <div className={styles.uploadItem}>
            <div className={styles.stepHeader}>
              <div className={styles.stepIcon}>
                <IconComponent size={24} />
              </div>
              <h3 className={styles.stepTitle}>{currentStep.title}</h3>
              <p className={styles.stepDescription}>{currentStep.description}</p>
            </div>

            <div {...currentStep.getRootProps({ className: styles.dropzone })}>
              <input {...currentStep.getInputProps()} />
              {files[currentStep.field] ? (
                <div className={styles.uploadedFile}>
                  <img
                    src={URL.createObjectURL(files[currentStep.field]) || "/placeholder.svg"}
                    alt={currentStep.title}
                    className={styles.preview}
                  />
                  <p className={styles.fileName}>{files[currentStep.field].name}</p>
                  <button onClick={(event) => handleRemove(currentStep.field, event)} className={styles.removeButton}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ) : (
                <div className={styles.dropzoneContent}>
                  <div className={styles.uploadIcon}>
                    <Upload size={32} />
                  </div>
                  <p className={styles.dropzoneText}>Toca aquí para seleccionar una imagen</p>
                  <span className={styles.dropzoneSubtext}>Formatos: JPG, PNG</span>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    )
  }

  const renderFullScreen = () => (
    <>
      <div className={styles.uploadSection}>
        {stepConfig.map((stepItem, index) => {
          const IconComponent = stepItem.icon
          return (
            <div key={index} className={styles.uploadItem}>
              <div className={styles.itemHeader}>
                <div className={styles.itemIcon}>
                  <IconComponent size={20} />
                </div>
                <div className={styles.itemInfo}>
                  <h4 className={styles.itemTitle}>{stepItem.title}</h4>
                  <p className={styles.itemDescription}>{stepItem.description}</p>
                </div>
              </div>

              <div {...stepItem.getRootProps({ className: styles.dropzone })}>
                <input {...stepItem.getInputProps()} />
                {files[stepItem.field] ? (
                  <div className={styles.uploadedFile}>
                    <img
                      src={URL.createObjectURL(files[stepItem.field]) || "/placeholder.svg"}
                      alt={stepItem.title}
                      className={styles.preview}
                    />
                    <p className={styles.fileName}>{files[stepItem.field].name}</p>
                    <button onClick={(event) => handleRemove(stepItem.field, event)} className={styles.removeButton}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <div className={styles.dropzoneContent}>
                    <div className={styles.uploadIcon}>
                      <Upload size={28} />
                    </div>
                    <p className={styles.dropzoneText}>Arrastra tu imagen aquí o haz clic para seleccionar</p>
                    <span className={styles.dropzoneSubtext}>Formatos: JPG, PNG</span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className={styles.submitButtonContainer}>
        <button className={styles.submitButton} disabled={!isFormValid} onClick={handleSubmit}>
          <CheckCircle size={18} />
          Enviar mis fotos
        </button>
      </div>
    </>
  )

  return (
    <div className={styles.uploadContainer}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerIcon}>
            <Shield size={24} />
          </div>
          <div className={styles.headerText}>
            <h2 className={styles.title}>Verifica tu identidad</h2>
            <p className={styles.subtitle}>
              Para garantizar la seguridad, necesitamos verificar tu identidad con estos documentos
            </p>
          </div>
        </div>
        <button className={styles.closeButton} onClick={closeModal}>
          <X size={20} />
        </button>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          <p>{error}</p>
        </div>
      )}

      {isMobile ? renderMobileSteps() : renderFullScreen()}

      {isMobile && (
        <>
          <div className={styles.stepsNavigation}>
            {step > 0 && (
              <button className={styles.navButton} onClick={prevStep}>
                <ArrowLeft size={16} />
                Anterior
              </button>
            )}
            {step < 2 ? (
              <button className={styles.navButton} onClick={nextStep} disabled={!files[stepConfig[step].field]}>
                Siguiente
                <ArrowRight size={16} />
              </button>
            ) : (
              <button className={styles.submitButton} disabled={!isFormValid} onClick={handleSubmit}>
                <CheckCircle size={16} />
                Enviar fotos
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default UploadPhotos








